import { isEmpty } from '@fruitful/fruitils';
import {
    IncomeSplitExecutions,
    IncomeSplitMonthNavigator,
    type IncomeSplitOverviewData,
    type IncomeSplitOverviewHistory,
    IncomeSplitProgressOverview,
} from '@fruitful/income-split';
import {
    getCategorizedRulesAndTotals,
    MoneyMapEmbeddedVisual,
} from '@fruitful/money-map';
import {
    AnalyticsUserEvent,
    MoneyMapAccountStatus,
    MoneyMapAdvisoryMode,
    MoneyMapAuthorType,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { type RelativePathString, router } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    Animations,
    ArrowRightIcon,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    H2Regular,
    Icon,
    InsetContainer,
    InsetContainerVariant,
    MoneyMapIcon,
    Separator,
    Sheet,
    SmoothieScrollView,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import MoneyMapNoNumbers from '../../../assets/images/money-map/money-map-no-numbers.svg';
import { useGetIncomeSplitOverviewFromGlobalState } from '../../banking/components/income-split/hooks/useGetIncomeSplitOverviewFromGlobalState';
import { trackDepositDetailsClicked } from '../../banking/components/income-split/overview/trackDepositDetailsClicked';
import TabHeader from '../../common/components/TabHeader';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import GoalsSummary from '../../goals/components/summary/GoalsSummary';
import { track } from '../../logger/Logger';
import { type UserMembership } from '../../user/types/User';
import { useGetMembershipMoneyMapAccounts } from '../hooks/useGetMembershipMoneyMapAccounts';
import { useMoneyMapWorkflow } from '../hooks/useMoneyMapWorkflow';
import { useMoneySystemTab } from '../hooks/useMoneySystemTab';
import {
    MoneyMapResetState,
    useResetMoneyMap,
} from '../hooks/useResetMoneyMap';
import { useRoadmapFeature } from '../hooks/useRoadmapFeature';
import {
    MoneyMapEarningsTileForDirectDeposit,
    MoneyMapEarningsTileForMoneyMap,
} from './MoneyMapEarnings';

const MM_DESCRIPTION =
    'A supportive money system that keeps you on track effortlessly. Spend from your designated spending account while bills and savings are handled automatically.';

const MoneyMapLemonCard = ({
    description,
    buttonText,
    onPress,
}: {
    description: string;
    buttonText: string;
    onPress: () => void;
}) => (
    <YStack
        width="100%"
        maxWidth={500}
        backgroundColor="$lemonLight"
        borderRadius={24}
        padding={24}
        gap={20}
        {...Animations.FadeInFromAbove}
    >
        <XStack gap={12} alignItems="flex-start">
            <Icon icon={MoneyMapIcon} size={32} color="$black" />
            <YStack gap={4} flex={1}>
                <BodyLargeEmphasized>
                    Your new Money Map is here!
                </BodyLargeEmphasized>
                <BodyMediumRegular color="$gray1000">
                    {description}
                </BodyMediumRegular>
            </YStack>
        </XStack>
        <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.LARGE}
            text={buttonText}
            onPress={onPress}
        />
    </YStack>
);

const ReviewUpdatedMoneyMapCard = ({
    membership,
}: {
    membership?: UserMembership | null;
}) => {
    const guideName = membership?.guide?.firstName ?? 'Your Guide';
    const { workflowSteps, isWorkflowCompleted } = useMoneyMapWorkflow();
    const isWaitingOnPartner =
        !isWorkflowCompleted &&
        workflowSteps.every((step) => step.completeness.isCompleteForMember);

    return (
        <MoneyMapLemonCard
            description={`${guideName} created a new Money Map for you to review and set up.`}
            buttonText={
                isWaitingOnPartner
                    ? 'View setup progress'
                    : 'Review updated Money Map'
            }
            onPress={() => {
                track(AnalyticsUserEvent.OPEN_MONEY_MAP_WORKFLOW, {
                    appContext: 'money-system-tab',
                });
                router.navigate('/roadmap/go/start');
            }}
        />
    );
};

const ExplorePreviewMoneyMapCard = ({
    moneyMapId,
    membership,
}: {
    moneyMapId: string;
    membership?: UserMembership | null;
}) => {
    const guideName = membership?.guide?.firstName ?? 'Your Guide';
    return (
        <MoneyMapLemonCard
            description={`${guideName} created a new Money Map for you to review.`}
            buttonText="Explore updated Money Map"
            onPress={() => {
                track(AnalyticsUserEvent.OPEN_PREVIEW_MONEY_MAP, {
                    appContext: 'money-system-tab',
                });
                router.navigate({
                    pathname: '/roadmap/money-map',
                    params: { moneyMapId },
                });
            }}
        />
    );
};

type MoneySystemTileType =
    | 'preview'
    | 'setup'
    | 'completed'
    | 'loading'
    | 'empty'
    | 'error';

const MoneySystemActivityCard = ({
    moneyMap,
    isMoneyMapLoading,
    treatAsRunning = false,
}: {
    moneyMap?: MoneyMapWithRulesAndAccounts;
    isMoneyMapLoading: boolean;
    treatAsRunning?: boolean;
}) => {
    const { hasMoneyMapError, isWorkflowCompleted, isLoadingCompleteness } =
        useMoneyMapWorkflow();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);

    // Under GOYO, a member who hasn't opened any accounts yet (cash accounts
    // step still incomplete) is routed to the onboarding review-numbers screen
    // instead of the GO flow — matching the home "Activate your money system"
    // card.
    const hasNotOpenedAccounts = moneyMap?.moneyMapRules?.every(
        (rule) =>
            rule.moneyMapAccount.status === MoneyMapAccountStatus.PROPOSED,
    );

    // A guide-authored money map (e.g. a guide duplicated/edited a CMM map)
    // exits the GOYO/robo experience, so fall back to the classic
    // "Set up your Money Map" routing (/roadmap/go/start).
    const isGuideAuthoredMoneyMap =
        moneyMap?.createdByType === MoneyMapAuthorType.GUIDE;

    const { data: accounts } = useGetMembershipMoneyMapAccounts({});

    const { billsTotalCents, spendTotalCents, goalsTotalCents } = useMemo(
        () => getCategorizedRulesAndTotals(moneyMap, accounts),
        [JSON.stringify(accounts), JSON.stringify(moneyMap)],
    );

    const status = moneyMap?.status;
    let type: MoneySystemTileType = 'empty';

    if (isMoneyMapLoading || isLoadingCompleteness) {
        type = 'loading';
    } else if (hasMoneyMapError) {
        type = 'error';
    } else if (treatAsRunning && moneyMap != null) {
        // Live-split source: show the running "Your Money Map" card even when
        // the map is INACTIVE or go-flow is still incomplete.
        type = 'completed';
    } else if (status === MoneyMapStatus.PREVIEW) {
        type = 'preview';
    } else if (status === MoneyMapStatus.ACTIVE && !isWorkflowCompleted) {
        type = 'setup';
    } else if (status === MoneyMapStatus.ACTIVE && isWorkflowCompleted) {
        type = 'completed';
    }

    const hasLiveData =
        moneyMap != null &&
        billsTotalCents != null &&
        spendTotalCents != null &&
        goalsTotalCents != null;

    const openMoneyMap = (eventType: AnalyticsUserEvent) => {
        if (moneyMap?.id == null) {
            return;
        }
        track(eventType, {
            appContext: 'money-system-tab',
        });
        router.navigate({
            pathname: '/roadmap/money-map',
            params: { moneyMapId: moneyMap.id },
        });
    };

    let title: string;
    let buttonLabel: string | undefined;
    let onPress: (() => void) | undefined;

    const isGoyoMoneyMap =
        moneyMap?.advisoryMode === MoneyMapAdvisoryMode.ROBO_ADVISOR_NO_IM;
    const goyoNextStepUrl = (
        hasNotOpenedAccounts
            ? '/onboarding/review-numbers'
            : '/roadmap/go/start'
    ) as RelativePathString;

    switch (type) {
        case 'loading':
            title = '';
            break;
        case 'error':
            title = 'Fruitful Money Map';
            break;
        case 'preview':
            title = 'Your Money Map is here!';
            buttonLabel = 'Explore your Money Map';
            onPress = () => {
                openMoneyMap(AnalyticsUserEvent.OPEN_PREVIEW_MONEY_MAP);
            };
            break;
        case 'setup':
            title = 'Your Money Map is here!';
            buttonLabel = 'Set up your Money Map';
            onPress = () => {
                track(AnalyticsUserEvent.OPEN_MONEY_MAP_WORKFLOW, {
                    appContext: 'money-system-tab',
                });
                if (
                    goyoEnabled &&
                    isGoyoMoneyMap &&
                    !isGuideAuthoredMoneyMap &&
                    hasNotOpenedAccounts
                ) {
                    router.navigate(goyoNextStepUrl);
                    return;
                }
                router.navigate('/roadmap/go/start');
            };
            break;
        case 'completed':
            title = 'Your Money Map';
            onPress = () => {
                openMoneyMap(AnalyticsUserEvent.OPEN_COMPLETED_MONEY_MAP);
            };
            break;
        case 'empty':
        default:
            title = 'Fruitful Money Map';
            buttonLabel = 'Ask your Guide about this';
            onPress = () => {
                track(AnalyticsUserEvent.OPEN_MONEY_MAP_GUIDE_PROMPT, {
                    appContext: 'money-system-tab',
                });
                router.navigate('/home/my-guide');
            };
    }

    const lastUpdatedLabel =
        moneyMap?.updatedAt != null
            ? `Last updated ${new Date(moneyMap.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : undefined;

    return (
        <YStack
            width="100%"
            maxWidth={500}
            gap={16}
            {...Animations.FadeInFromAbove}
        >
            <YStack
                borderRadius={24}
                backgroundColor="$gray100"
                overflow="hidden"
                {...(type === 'completed' && onPress != null
                    ? { onPress, cursor: 'pointer' }
                    : {})}
            >
                <YStack backgroundColor="$gray200" padding={24} gap={8}>
                    {type === 'loading' ? (
                        <Spinner height={50} {...Animations.FadeIn} />
                    ) : (
                        <>
                            <XStack
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <YStack gap={4} flex={1}>
                                    <BodyLargeEmphasized>
                                        {title}
                                    </BodyLargeEmphasized>
                                    {type === 'completed' &&
                                        lastUpdatedLabel != null && (
                                            <BodySmallRegular color="$gray1000">
                                                {lastUpdatedLabel}
                                            </BodySmallRegular>
                                        )}
                                </YStack>
                                {type === 'completed' && (
                                    <Icon
                                        icon={ArrowRightIcon}
                                        size={24}
                                        color="$gray1000"
                                    />
                                )}
                            </XStack>
                            {type !== 'error' && type !== 'completed' && (
                                <BodyMediumRegular opacity={0.8}>
                                    {MM_DESCRIPTION}
                                </BodyMediumRegular>
                            )}
                        </>
                    )}
                </YStack>
                <YStack
                    backgroundColor="$gray200"
                    padding={24}
                    gap={16}
                    alignItems="center"
                >
                    {hasLiveData ? (
                        <MoneyMapEmbeddedVisual
                            incomeTotalCents={moneyMap.monthlyIncomeCents}
                            billsTotalCents={billsTotalCents}
                            spendTotalCents={spendTotalCents}
                            goalsTotalCents={goalsTotalCents}
                        />
                    ) : (
                        <MoneyMapNoNumbers />
                    )}
                    {type !== 'loading' &&
                        type !== 'completed' &&
                        !isEmpty(buttonLabel) &&
                        onPress != null && (
                            <View width="100%" {...Animations.FadeIn}>
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    text={buttonLabel ?? ''}
                                    onPress={onPress}
                                />
                            </View>
                        )}
                </YStack>
            </YStack>
            {type === 'completed' && (
                <MoneyMapEarningsTileForDirectDeposit
                    variant="dual-action"
                    moneyMap={moneyMap}
                    isMoneyMapCompleted={true}
                    appContext="money-system-tab"
                />
            )}
            {moneyMap != null && (type === 'preview' || type === 'setup') && (
                <MoneyMapEarningsTileForMoneyMap
                    moneyMap={moneyMap}
                    isMoneyMapCompleted={false}
                    appContext="money-system-tab"
                />
            )}
        </YStack>
    );
};

const IncomeSplitSection = () => {
    const { data, isLoading } = useGetIncomeSplitOverviewFromGlobalState();

    if (isLoading) {
        return null;
    }

    if (data == null || data.history.length === 0) {
        return null;
    }

    const hasAnyExecutions = data.history.some(
        (month) => month.executions.length > 0,
    );

    if (!hasAnyExecutions) {
        return null;
    }

    // Mount only after overview data exists so month index can initialize to
    // the current month (same as banking IncomeSplitOverview).
    return <IncomeSplitSectionContent data={data} />;
};

const IncomeSplitSectionContent = ({
    data,
}: {
    data: IncomeSplitOverviewData;
}) => {
    const [currentMonthIndex, setCurrentMonthIndex] = useState(
        () => data.history.length - 1,
    );

    const selectedMonth = data.history[
        currentMonthIndex
    ] as IncomeSplitOverviewHistory;

    const months = data.history.filter((month) => month.executions.length > 0);
    const hasPreviousMonths = months.length > 1;

    return (
        <YStack width="100%" maxWidth={500} {...Animations.FadeInFromAbove}>
            <InsetContainer variant={InsetContainerVariant.DARK}>
                <YStack>
                    <IncomeSplitMonthNavigator
                        data={data}
                        currentMonthIndex={currentMonthIndex}
                        setCurrentMonthIndex={setCurrentMonthIndex}
                    />
                    {selectedMonth.progress != null && (
                        <YStack paddingVertical={16}>
                            <IncomeSplitProgressOverview
                                progress={selectedMonth.progress}
                            />
                        </YStack>
                    )}
                    <IncomeSplitExecutions
                        month={selectedMonth}
                        onPressExecution={trackDepositDetailsClicked}
                        onSelectExecution={(execution) => {
                            router.push({
                                pathname: '/income-split/overview',
                                params: { executionId: execution.id },
                            });
                        }}
                    />
                </YStack>
            </InsetContainer>
            {hasPreviousMonths && (
                <View alignItems="center" marginTop={16}>
                    <View
                        paddingVertical={12}
                        paddingHorizontal={20}
                        borderRadius={100}
                        backgroundColor="$gray200"
                        onPress={() => {
                            router.push('/income-split/all-months');
                        }}
                        cursor="pointer"
                    >
                        <BodyMediumRegular>View all months</BodyMediumRegular>
                    </View>
                </View>
            )}
        </YStack>
    );
};

const MoneySystemDevToolsSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { isLoadingMoneyMap, moneyMap, refetchMoneyMapCompleteness } =
        useMoneyMapWorkflow();
    const { mutateAsync: resetMoneyMap } = useResetMoneyMap();
    const toast = useToastController();

    const resetSubmit = async (resetState: MoneyMapResetState) => {
        toast.show(`Reset starting...`);
        await resetMoneyMap({
            body: {
                moneyMapId: moneyMap?.id ?? '',
                resetState,
            },
        });
        toast.show(`Reset Complete!`);
        void refetchMoneyMapCompleteness();
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={true}>
            <SmoothieScrollView>
                <YStack alignItems="center" gap={16} paddingBottom={40}>
                    <BodyMediumEmphasized>
                        Money Map Development Tools
                    </BodyMediumEmphasized>

                    <YStack gap={12} width="100%" maxWidth={400}>
                        <Separator marginTop={16} />
                        <BodyMediumEmphasized>Shortcuts</BodyMediumEmphasized>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Start"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/start');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Money Map"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/money-map');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open cash accounts"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/cash-accounts');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open card accounts"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/card-accounts');
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Open investment accounts"
                            onPress={() => {
                                onClose();
                                router.navigate(
                                    '/roadmap/go/investment-accounts',
                                );
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Income Split"
                            onPress={() => {
                                onClose();
                                router.navigate('/roadmap/go/income-split');
                            }}
                        />
                    </YStack>

                    <YStack gap={12} width="100%" maxWidth={400}>
                        <Separator marginTop={16} />
                        <BodyMediumEmphasized>Reset</BodyMediumEmphasized>
                        {isLoadingMoneyMap || moneyMap == null ? (
                            <Spinner alignSelf="center" />
                        ) : (
                            <>
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Income Split"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_INCOME_SPLIT,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Opening IM Accounts + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_OPENING_APEX_ACCOUNTS,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Opening Accounts + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_OPENING_ACCOUNTS,
                                        );
                                    }}
                                />
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Reset Money Map to Draft + above"
                                    onPress={() => {
                                        void resetSubmit(
                                            MoneyMapResetState.RESET_MONEY_MAP_STATUS_TO_DRAFT,
                                        );
                                    }}
                                />
                            </>
                        )}
                    </YStack>
                </YStack>
            </SmoothieScrollView>
        </Sheet>
    );
};

export const RoadmapMoneySystem = () => {
    const { disableRoadmapFeatures } = useRoadmapFeature();
    const {
        workflowMoneyMap,
        previewMoneyMap,
        liveSplitMoneyMap,
        currentIncomeSplit,
        isLoading,
        showReviewUpdatedCard,
        showExplorePreviewCard,
        showSeparateWorkflowCard,
        showIncomeSplit,
    } = useMoneySystemTab();
    const { membership } = useAuthedUser();
    const [isDevSheetOpen, setIsDevSheetOpen] = useState(false);
    useTrackViewOnFocus(
        disableRoadmapFeatures ? 'RoadmapGoalsSummary' : 'MoneySystem',
    );

    if (disableRoadmapFeatures) {
        return <GoalsSummary includeTabHeader={true} />;
    }

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <View flex={1}>
            <TabHeader headerTitle="Money System" />
            <SmoothieScrollView
                containerStyle={{
                    marginHorizontal: 24,
                    $sm: { width: '100%', marginHorizontal: 0 },
                }}
            >
                <YStack
                    gap={16}
                    marginBottom={50}
                    $sm={{ paddingHorizontal: 36 }}
                    width="100%"
                    maxWidth={1100}
                    alignSelf="center"
                    alignItems="center"
                >
                    {isLoading ? (
                        <MoneySystemActivityCard isMoneyMapLoading={true} />
                    ) : (
                        <>
                            {showExplorePreviewCard &&
                            previewMoneyMap != null ? (
                                <ExplorePreviewMoneyMapCard
                                    moneyMapId={previewMoneyMap.id}
                                    membership={membership}
                                />
                            ) : showReviewUpdatedCard &&
                              currentIncomeSplit != null ? (
                                <ReviewUpdatedMoneyMapCard
                                    membership={membership}
                                />
                            ) : showReviewUpdatedCard &&
                              currentIncomeSplit == null ? (
                                <MoneySystemActivityCard
                                    moneyMap={workflowMoneyMap}
                                    isMoneyMapLoading={false}
                                />
                            ) : showSeparateWorkflowCard ? (
                                <MoneySystemActivityCard
                                    moneyMap={workflowMoneyMap}
                                    isMoneyMapLoading={false}
                                />
                            ) : null}
                            {liveSplitMoneyMap != null ? (
                                <MoneySystemActivityCard
                                    moneyMap={liveSplitMoneyMap}
                                    isMoneyMapLoading={false}
                                    treatAsRunning={true}
                                />
                            ) : showReviewUpdatedCard ? null : (
                                <MoneySystemActivityCard
                                    moneyMap={workflowMoneyMap}
                                    isMoneyMapLoading={false}
                                />
                            )}
                        </>
                    )}
                    {showIncomeSplit && <IncomeSplitSection />}
                </YStack>
            </SmoothieScrollView>
            {!IS_PRODUCTION && (
                <>
                    <XStack
                        gap={8}
                        position="absolute"
                        bottom={20}
                        right={20}
                        zIndex={999}
                    >
                        <View
                            onPress={() => {
                                setIsDevSheetOpen(true);
                            }}
                            cursor="pointer"
                            backgroundColor="$gray200"
                            borderRadius={100}
                            width={40}
                            height={40}
                            alignItems="center"
                            justifyContent="center"
                            accessibilityRole="button"
                            accessibilityLabel="Open dev tools"
                        >
                            <H2Regular>🛠️</H2Regular>
                        </View>
                    </XStack>
                    <MoneySystemDevToolsSheet
                        isOpen={isDevSheetOpen}
                        onClose={() => {
                            setIsDevSheetOpen(false);
                        }}
                    />
                </>
            )}
        </View>
    );
};
